import Chip from '@mui/material/Chip';
import { common } from '@mui/material/colors';
import type { Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';
import React from 'react';

import { Neutrals, SemanticError, SemanticInfo, SemanticSuccess, Warning } from '../../color-palette/colors';

export type StatusTone = 'neutral' | 'info' | 'success' | 'warning' | 'error';
export type StatusEmphasis = 'subtle' | 'strong' | 'outline' | 'dotted' | 'dashed';
export type StatusChipSize = 'small' | 'medium';
export type StatusBorderStyle = 'solid' | 'dotted' | 'dashed';

export interface StatusChipColors {
  readonly fg: string;
  readonly bg: string;
  readonly border?: string;
  readonly borderStyle?: StatusBorderStyle;
}

interface StatusChipBaseProps {
  label: React.ReactNode;
  icon?: React.ReactElement;
  size?: StatusChipSize;
}

interface StatusChipToneProps extends StatusChipBaseProps {
  tone: StatusTone;
  emphasis?: StatusEmphasis;
  fg?: never;
  bg?: never;
  border?: never;
  borderStyle?: never;
}

interface StatusChipPairProps extends StatusChipBaseProps, StatusChipColors {
  tone?: never;
  emphasis?: never;
}

export type StatusChipProps = StatusChipToneProps | StatusChipPairProps;

const WHITE = common.white;
const OUTLINE_BORDER = Neutrals[300];
const BROKEN_BORDER = Neutrals[500];

const OUTLINE: Readonly<Record<StatusTone, StatusChipColors>> = {
  neutral: { fg: Neutrals[700], bg: 'transparent', border: OUTLINE_BORDER },
  info: { fg: SemanticInfo[900], bg: 'transparent', border: OUTLINE_BORDER },
  success: { fg: SemanticSuccess[900], bg: 'transparent', border: OUTLINE_BORDER },
  warning: { fg: Warning[800], bg: 'transparent', border: OUTLINE_BORDER },
  error: { fg: SemanticError[900], bg: 'transparent', border: OUTLINE_BORDER },
};

const brokenOutline = (borderStyle: StatusBorderStyle): Readonly<Record<StatusTone, StatusChipColors>> => ({
  neutral: { ...OUTLINE.neutral, border: BROKEN_BORDER, borderStyle },
  info: { ...OUTLINE.info, border: BROKEN_BORDER, borderStyle },
  success: { ...OUTLINE.success, border: BROKEN_BORDER, borderStyle },
  warning: { ...OUTLINE.warning, border: BROKEN_BORDER, borderStyle },
  error: { ...OUTLINE.error, border: BROKEN_BORDER, borderStyle },
});

export const STATUS_CHIP_COLORS: Readonly<Record<StatusEmphasis, Readonly<Record<StatusTone, StatusChipColors>>>> = {
  subtle: {
    neutral: { fg: Neutrals[700], bg: Neutrals[50] },
    info: { fg: SemanticInfo[900], bg: SemanticInfo[50] },
    success: { fg: SemanticSuccess[900], bg: SemanticSuccess[50] },
    warning: { fg: Warning[800], bg: Warning[50] },
    error: { fg: SemanticError[900], bg: SemanticError[50] },
  },
  strong: {
    neutral: { fg: WHITE, bg: Neutrals[700] },
    info: { fg: WHITE, bg: SemanticInfo[900] },
    success: { fg: WHITE, bg: SemanticSuccess[900] },
    warning: { fg: WHITE, bg: Warning[800] },
    error: { fg: WHITE, bg: SemanticError[600] },
  },
  outline: OUTLINE,
  dotted: brokenOutline('dotted'),
  dashed: brokenOutline('dashed'),
};

const hasTone = (props: StatusChipProps): props is StatusChipToneProps => props.tone !== undefined;

const colorsOf = (props: StatusChipProps): StatusChipColors => {
  if (hasTone(props)) return STATUS_CHIP_COLORS[props.emphasis ?? 'subtle'][props.tone];
  return { fg: props.fg, bg: props.bg, border: props.border, borderStyle: props.borderStyle };
};

const LABEL_VARIANT: Record<StatusChipSize, 'caption2' | 'label'> = { small: 'caption2', medium: 'label' };
const LABEL_PADDING_INLINE: Record<StatusChipSize, number> = { small: 2, medium: 3 };

const chipSx =
  ({ fg, bg, border, borderStyle = 'solid' }: StatusChipColors, size: StatusChipSize) =>
  (theme: Theme): SystemStyleObject<Theme> => ({
    height: 'auto',
    color: fg,
    backgroundColor: bg,
    border: border ? `1px ${borderStyle} ${border}` : undefined,
    '& .MuiChip-label': {
      ...theme.typography[LABEL_VARIANT[size]],
      color: 'inherit',
      fontWeight: theme.typography.fontWeightBold,
      paddingBlock: 1,
      paddingInline: LABEL_PADDING_INLINE[size],
    },
    '& .MuiChip-icon': { color: 'inherit', fontSize: '1.2em' },
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
  });

export const StatusChip = React.memo<StatusChipProps>((props) => {
  const { label, icon, size = 'small' } = props;
  return <Chip size="small" icon={icon} label={label} sx={chipSx(colorsOf(props), size)} />;
});
StatusChip.displayName = 'StatusChip';
